/**
 * SaveConfirmationModal - Confirmation dialog before saving power reassignments
 * Shows a summary of all pending transfers grouped by direction
 */

import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { OathModal, OathButton } from '../../../shared/OathUI';

const SaveConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  saving,
  pendingQueue = { movedToAgency2: [], movedToAgency1: [] },
  agency1Name,
  agency2Name,
}) => {
  const totalCount = pendingQueue.movedToAgency2.length + pendingQueue.movedToAgency1.length;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm Power Reassignment"
      size="md"
      footer={
        <>
          <OathButton variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </OathButton>
          <OathButton
            variant="primary"
            onClick={onConfirm}
            disabled={saving}
            loading={saving}
            data-testid="modal-confirm"
          >
            Confirm &amp; Save
          </OathButton>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          You are about to reassign{' '}
          <span className="font-semibold text-gray-900 dark:text-white">{totalCount} power(s)</span>.
          This action cannot be undone once saved.
        </p>

        {pendingQueue.movedToAgency2.length > 0 && (
          <div className="border-l-2 border-primary pl-3">
            <div className="flex items-center gap-1.5 mb-2">
              <ArrowRightIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              <span className="text-xs font-medium text-primary uppercase tracking-wider">
                To {agency2Name}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {pendingQueue.movedToAgency2.map((powerNo) => (
                <span
                  key={powerNo}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                >
                  {powerNo}
                </span>
              ))}
            </div>
          </div>
        )}

        {pendingQueue.movedToAgency1.length > 0 && (
          <div className="border-l-2 border-warning pl-3">
            <div className="flex items-center gap-1.5 mb-2">
              <ArrowRightIcon className="h-3.5 w-3.5 text-warning flex-shrink-0" />
              <span className="text-xs font-medium text-warning uppercase tracking-wider">
                To {agency1Name}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {pendingQueue.movedToAgency1.map((powerNo) => (
                <span
                  key={powerNo}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                >
                  {powerNo}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </OathModal>
  );
};

export default SaveConfirmationModal;
